// src/pages/Education/PreschoolFormModal.jsx
// Модальная форма создания ДДО (UC.13.02, атрибутный состав — Табл. 81 СТПО).
// antd 4/5: Form.useForm() + декларативные <Form.Item name rules>. Режим create-only.
//
// Валидация (АС2): обязательные → «Поле обязательно…»; числовые — целое ≥0, иначе
// «Допускается только целое…»; координаты — число ≤6 знаков. <Form scrollToFirstError>
// сам скроллит к первому ошибочному полю. Серверные 422 — через form.setFields.
//
// Смена языка при открытой форме НЕ теряет введённое: значения живут в form store,
// перерисовываются только подписи (закрывает пограничный случай ТЗ п.8).
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Alert, Row, Col } from 'antd';
import { createPreschool, ApiError } from './api';
import { occState, occDeviation, STATE_I18N_KEY } from './occupancy';

const { Option } = Select;

// Целое ≥0 (до 9 знаков) и координата (число, ≤6 знаков дробной части).
const INT_RE = /^\d{1,9}$/;
const COORD_RE = /^-?\d{1,3}([.,]\d{1,6})?$/;

const toInt = (x) => (x != null && INT_RE.test(String(x)) ? parseInt(x, 10) : null);

function buildPayload(v) {
  const int = (x) => (x == null || x === '' ? null : parseInt(x, 10));
  const flt = (x) => {
    if (x == null || x === '') return null;
    const n = parseFloat(String(x).replace(',', '.'));
    return Number.isNaN(n) ? null : n;
  };
  return {
    // Одно поле наименования на оба языка — упрощение (открытый вопрос: нужны ли
    // раздельные name/name_kz в форме).
    name: (v.name || '').trim(),
    nameKz: (v.name || '').trim(),
    addr: (v.addr || '').trim(),
    addrKz: (v.addr || '').trim(),
    district: v.district,
    lat: flt(v.lat),
    lon: flt(v.lon),
    form: v.form,
    type: v.type,
    lang: v.lang,
    time: v.time,
    seats: int(v.seats),
    kont: int(v.kont),
    free: int(v.free),
    queue: int(v.queue),
    napr: int(v.napr),
    zach: int(v.zach),
    period: v.period,
    // Значение из локального словаря — '1'/'0'; из /meta может прийти true/'true'.
    active: v.active === '1' || v.active === true || v.active === 'true',
  };
}

export default function PreschoolFormModal({ visible, lang, meta, t, onCancel, onCreated }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [topErrorKey, setTopErrorKey] = useState(null);
  const [live, setLive] = useState({ seats: null, kont: null }); // для предпросмотра

  const dict = (meta && meta.dictionaries) || {};

  // При каждом открытии — чистая форма (АС1 гарантирует закрытие без сохранения).
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setTopErrorKey(null);
      setLive({ seats: null, kont: null });
    }
  }, [visible, form]);

  // Все пути закрытия (кнопка «Отмена», крестик, Esc, клик по маске) → сюда (АС1).
  const handleCancel = () => onCancel();

  const applyServerError = (e) => {
    if (e instanceof ApiError && e.status === 422 && e.body && e.body.fields) {
      form.setFields(
        Object.keys(e.body.fields).map((name) => ({
          name,
          errors: [e.body.fields[name]],
        }))
      );
      setTopErrorKey('alertReq');
    } else {
      setTopErrorKey('alertServer');
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        setSubmitting(true);
        setTopErrorKey(null);
        return createPreschool(buildPayload(values))
          .then((created) => {
            setSubmitting(false);
            onCreated(created); // родитель закроет модалку, покажет тост, обновит журнал
          })
          .catch((e) => {
            setSubmitting(false);
            applyServerError(e);
          });
      })
      .catch(() => {
        // ошибки клиентской валидации: поля подсвечены, scrollToFirstError навёл фокус
        setTopErrorKey('alertReq');
      });
  };

  const onValuesChange = (_, all) => {
    setLive({ seats: toInt(all.seats), kont: toInt(all.kont) });
  };

  // Правило для целого числа: required- и optional-вариант.
  const intRules = (required) => {
    const r = [{ pattern: INT_RE, message: t('numMsg') }];
    if (required) r.unshift({ required: true, message: t('reqMsg') });
    return r;
  };
  const req = [{ required: true, message: t('reqMsg') }];
  const selectPh = t('selPlaceholder');
  const optList = (group) => (dict[group] || []).map((o) => (
    <Option key={o.v} value={o.v}>{o[lang]}</Option>
  ));

  // Live-предпросмотр отклонения и состояния наполненности.
  const dev = occDeviation(live.seats, live.kont);
  const devTxt = dev == null ? '—' : dev.toLocaleString('ru-RU');
  const normMin = meta.thresholds.occ_norm_min;
  const stTxt =
    live.seats != null && live.kont != null
      ? t(STATE_I18N_KEY[occState(live.seats, live.kont, normMin)])
      : '—';

  return (
    <Modal
      title={t('mTitle')}
      open={visible}
      visible={visible} /* antd4: visible, antd5: open — передаём оба для совместимости */
      width={760}
      onCancel={handleCancel}
      maskClosable
      keyboard
      className="ddo-modal"
      footer={[
        <span key="note" className="ddo-foot-note">{t('footNote')}</span>,
        <Button key="cancel" onClick={handleCancel}>{t('cancel')}</Button>,
        <Button key="save" type="primary" loading={submitting} onClick={handleSave}>
          {t('save')}
        </Button>,
      ]}
    >
      {topErrorKey && (
        <Alert type="error" showIcon style={{ marginBottom: 14 }} message={t(topErrorKey)} />
      )}

      <Form
        form={form}
        layout="vertical"
        colon={false}
        scrollToFirstError
        onValuesChange={onValuesChange}
        initialValues={{ district: (dict.district && dict.district[0] && dict.district[0].v) || undefined }}
      >
        {/* ── Сведения об организации ── */}
        <div className="ddo-sec-cap">{t('secGeneral')}</div>
        <Row gutter={14}>
          <Col span={24}>
            <Form.Item name="name" label={t('fName')} rules={req}>
              <Input maxLength={250} showCount placeholder={t('phName')} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="addr" label={t('fAddr')} rules={req}>
              <Input maxLength={250} showCount placeholder={t('phAddr')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="district" label={t('fDistrict')} rules={req}>
              <Select>{optList('district')}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* Координаты — одна подпись, два поля; каждое со своей ошибкой формата. */}
            <div className="ddo-field-label">{t('fCoords')}</div>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="lat" rules={[{ pattern: COORD_RE, message: t('numMsgCoords') }]}>
                  <Input inputMode="decimal" placeholder={t('phLat')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="lon" rules={[{ pattern: COORD_RE, message: t('numMsgCoords') }]}>
                  <Input inputMode="decimal" placeholder={t('phLon')} />
                </Form.Item>
              </Col>
            </Row>
            <div className="ddo-field-hint">{t('hintCoords')}</div>
          </Col>
        </Row>

        {/* ── Классификация (разрезы дэшборда) ── */}
        <div className="ddo-sec-cap">{t('secClass')}</div>
        <Row gutter={14}>
          <Col span={12}>
            <Form.Item name="form" label={t('fForm')} rules={req}>
              <Select placeholder={selectPh}>{optList('form')}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="type" label={t('fTypeF')} rules={req}>
              <Select placeholder={selectPh}>{optList('type')}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lang" label={t('fLang')} extra={t('hintLang')} rules={req}>
              <Select placeholder={selectPh}>{optList('lang')}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="time" label={t('fTime')} rules={req}>
              <Select placeholder={selectPh}>{optList('time')}</Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ── Показатели наполненности и очереди ── */}
        <div className="ddo-sec-cap">{t('secCounts')}</div>
        <Row gutter={14}>
          <Col span={8}>
            <Form.Item name="seats" label={t('fSeats')} rules={intRules(true)}>
              <Input inputMode="numeric" placeholder={t('phSeats')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="kont" label={t('fKont')} rules={intRules(true)}>
              <Input inputMode="numeric" placeholder={t('phKont')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="free" label={t('fFree')} extra={t('optional')} rules={intRules(false)}>
              <Input inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="queue" label={t('fQueue')} extra={t('optional')} rules={intRules(false)}>
              <Input inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="napr" label={t('fNapr')} extra={t('optional')} rules={intRules(false)}>
              <Input inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="zach" label={t('fZach')} extra={t('optional')} rules={intRules(false)}>
              <Input inputMode="numeric" />
            </Form.Item>
          </Col>
        </Row>

        {/* Расчётная строка — считает ПО (RQ.24.05/24.06), здесь только предпросмотр. */}
        <div className="ddo-calc">
          <span>{t('calcDev')} <b>{devTxt}</b></span>
          <span>{t('calcState')} <b>{stTxt}</b></span>
          <span className="ddo-calc-hint">{t('calcHint')}</span>
        </div>

        {/* ── Отчётность и учёт ── */}
        <div className="ddo-sec-cap">{t('secMeta')}</div>
        <Row gutter={14}>
          <Col span={12}>
            <Form.Item name="period" label={t('fPeriod')} rules={req}>
              <Select placeholder={selectPh}>{optList('period')}</Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="active" label={t('fActive')} extra={t('hintActive')} rules={req}>
              <Select placeholder={selectPh}>{optList('active')}</Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
